import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getFavorites, getCachedFavorites, reconstructFavoriteIcon, FavoriteItem } from '../../utils/favorites';
import { getSidebarItems } from './config/SidebarItems';
import { useModuleEntitlements } from '../../modules/module-management';
import { MenuItem } from './Sidebar';
import FavoritesManager from './FavoritesManager';
import { cn } from '../../lib/utils';

// Icons
const StarIcon = ({ className = 'w-5 h-5', filled = false }: { className?: string; filled?: boolean }) => (
  <svg className={className} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

const ChevronDownIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const CogIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const XIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export type FavoritesDisplayMode = 'dropdown' | 'bar' | 'pins' | 'none';

interface FavoritesTopbarProps {
  displayMode: FavoritesDisplayMode;
  maxPins?: number; // For 'pins' mode, max items to show
}

interface FavoriteWithIcon extends FavoriteItem {
  icon?: React.ReactNode;
}

const FavoritesTopbar: React.FC<FavoritesTopbarProps> = ({ displayMode, maxPins = 5 }) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showManager, setShowManager] = useState(false);
  const { entitlements } = useModuleEntitlements();
  
  // Get sidebar items filtered by entitlements for icon reconstruction
  const baseMenuItems = React.useMemo(
    () => getSidebarItems(undefined, entitlements),
    [entitlements]
  );
  
  // Initialize with cached favorites for instant loading
  const [favorites, setFavorites] = useState<FavoriteWithIcon[]>(() => {
    const cached = getCachedFavorites();
    return cached.map(fav => ({
      ...fav,
      icon: reconstructFavoriteIcon(fav, baseMenuItems),
    }));
  });

  const loadFavorites = useCallback(async () => {
    try {
      const favs = await getFavorites();
      const favsWithIcons = favs.map(fav => ({
        ...fav,
        icon: reconstructFavoriteIcon(fav, baseMenuItems),
      }));
      setFavorites(favsWithIcons);
    } catch (error) {
      console.error('Error loading favorites:', error);
      // Keep cached favorites on error - don't clear
    }
  }, [baseMenuItems]);

  useEffect(() => {
    // Fetch fresh data from API (cache was already loaded in initial state)
    loadFavorites();

    const handleFavoritesUpdate = () => {
      loadFavorites();
    };

    window.addEventListener('favoritesUpdated', handleFavoritesUpdate);
    return () => {
      window.removeEventListener('favoritesUpdated', handleFavoritesUpdate);
    };
  }, [loadFavorites]);

  const handleManagerClose = () => {
    // Closing the manager should be instant; favorites utilities already
    // dispatch 'favoritesUpdated' when changes are actually saved.
    setShowManager(false);
  };

  if (displayMode === 'none') {
    return null;
  }

  // Mode 1: Dropdown
  if (displayMode === 'dropdown') {
    return (
      <>
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors',
              colors.textMuted,
              `hover:${colors.textSecondary}`,
              'focus:outline-none focus:ring-2 focus:ring-teal-600/40'
            )}
            title="Favorites"
          >
            <StarIcon className="w-5 h-5 text-yellow-500" filled />
            <span className={cn('text-sm font-medium hidden sm:inline', colors.textSecondary)}>
              Favorites
            </span>
            {favorites.length > 0 && (
              <span className="hidden sm:flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold bg-yellow-100 text-yellow-700 rounded-full">
                {favorites.length}
              </span>
            )}
            <ChevronDownIcon className={cn('w-4 h-4 transition-transform', showDropdown ? 'rotate-180' : '')} />
          </button>

          {showDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
              <div className={cn(
                'absolute right-0 mt-2 w-72 rounded-xl shadow-lg py-2 z-20 border',
                colors.surface,
                colors.border
              )}>
                {/* Header */}
                <div className={cn('px-4 py-2 border-b flex items-center justify-between', colors.border)}>
                  <span className={cn('text-sm font-semibold', colors.text)}>Favorites</span>
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      setShowManager(true);
                    }}
                    className={cn('p-1.5 rounded-lg transition-colors', colors.textMuted, 'hover:bg-gray-100 dark:hover:bg-gray-800')}
                    title="Manage Favorites"
                  >
                    <CogIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Favorites List */}
                <div className="max-h-80 overflow-y-auto">
                  {favorites.length > 0 ? (
                    favorites.map((fav, index) => (
                      <Link
                        key={index}
                        to={fav.to}
                        onClick={() => setShowDropdown(false)}
                        className={cn(
                          'flex items-center gap-3 px-4 py-2.5 transition-colors',
                          'hover:bg-gray-50 dark:hover:bg-gray-800'
                        )}
                      >
                        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', colors.surfaceHover)}>
                          {fav.icon || <StarIcon className="w-4 h-4 text-yellow-500" filled />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={cn('text-sm font-medium truncate', colors.text)}>{fav.title}</div>
                          {fav.fullPath && (
                            <div className={cn('text-xs truncate', colors.textMuted)}>{fav.fullPath}</div>
                          )}
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className={cn('px-4 py-6 text-center', colors.textMuted)}>
                      <StarIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No favorites yet</p>
                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          setShowManager(true);
                        }}
                        className="mt-2 text-sm text-teal-700 hover:text-teal-800 font-medium"
                      >
                        Add favorites
                      </button>
                    </div>
                  )}
                </div>

                {/* Footer */}
                {favorites.length > 0 && (
                  <div className={cn('px-4 py-2 border-t', colors.border)}>
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        setShowManager(true);
                      }}
                      className={cn('w-full text-sm font-medium text-teal-700 hover:text-teal-800 py-1')}
                    >
                      Manage Favorites
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <FavoritesManager open={showManager} onClose={handleManagerClose} />
      </>
    );
  }

  // Mode 2: Bar (renders below topbar, returns a bar component)
  if (displayMode === 'bar') {
    if (favorites.length === 0) {
      return null; // Don't show bar if no favorites
    }

    return (
      <>
        <div className={cn(
          'flex items-center gap-2 px-4 py-2 border-b overflow-x-auto',
          colors.surface,
          colors.border
        )}>
          <StarIcon className="w-4 h-4 text-yellow-500 flex-shrink-0" filled />
          <div className="flex items-center gap-2 flex-1 overflow-x-auto">
            {favorites.map((fav, index) => (
              <Link
                key={index}
                to={fav.to}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
                  colors.surfaceHover,
                  colors.text,
                  'hover:bg-teal-50 hover:text-teal-700 dark:hover:bg-teal-900/20 dark:hover:text-teal-400'
                )}
              >
                {fav.icon && <span className="w-4 h-4">{fav.icon}</span>}
                {fav.title}
              </Link>
            ))}
          </div>
          <button
            onClick={() => setShowManager(true)}
            className={cn('p-1.5 rounded-lg flex-shrink-0 transition-colors', colors.textMuted, 'hover:bg-gray-100 dark:hover:bg-gray-800')}
            title="Manage Favorites"
          >
            <CogIcon className="w-4 h-4" />
          </button>
        </div>

        <FavoritesManager open={showManager} onClose={handleManagerClose} />
      </>
    );
  }

  // Mode 3: Pins (icon buttons in topbar - centered)
  if (displayMode === 'pins') {
    const displayedFavorites = favorites.slice(0, maxPins);
    const hasMore = favorites.length > maxPins;

    return (
      <>
        <div className="flex items-center gap-2">
          {displayedFavorites.map((fav, index) => (
            <button
              key={index}
              onClick={() => navigate(fav.to)}
              className={cn(
                'p-2 rounded-lg transition-colors relative group',
                colors.textMuted,
                'hover:bg-gray-100 dark:hover:bg-gray-800',
                'focus:outline-none focus:ring-2 focus:ring-teal-600/40'
              )}
              title={fav.title}
            >
              {/* Wrapper to ensure consistent h-6 w-6 icon size */}
              <span className="flex items-center justify-center h-6 w-6 [&>svg]:h-6 [&>svg]:w-6">
                {fav.icon || <StarIcon className="h-6 w-6 text-yellow-500" filled />}
              </span>
              {/* Tooltip */}
              <span className={cn(
                'absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 text-xs font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50',
                `${colors.surface} ${colors.text}`
              )}>
                {fav.title}
              </span>
            </button>
          ))}

          {/* Show more indicator or manage button */}
          {(hasMore || favorites.length > 0) && (
            <button
              onClick={() => setShowManager(true)}
              className={cn(
                'p-2 rounded-lg transition-colors relative group',
                colors.textMuted,
                'hover:bg-gray-100 dark:hover:bg-gray-800',
                'focus:outline-none focus:ring-2 focus:ring-teal-600/40'
              )}
              title={hasMore ? `+${favorites.length - maxPins} more favorites` : 'Manage Favorites'}
            >
              {hasMore ? (
                <span className="flex items-center justify-center h-6 w-6 text-sm font-semibold">
                  +{favorites.length - maxPins}
                </span>
              ) : (
                <CogIcon className="h-6 w-6" />
              )}
            </button>
          )}

          {/* Empty state - just show add button */}
          {favorites.length === 0 && (
            <button
              onClick={() => setShowManager(true)}
              className={cn(
                'flex items-center gap-1.5 p-2 rounded-lg transition-colors',
                colors.textMuted,
                'hover:bg-gray-100 dark:hover:bg-gray-800',
                'focus:outline-none focus:ring-2 focus:ring-teal-600/40'
              )}
              title="Add Favorites"
            >
              <StarIcon className="h-6 w-6" />
              <span className="text-sm">Add</span>
            </button>
          )}
        </div>

        <FavoritesManager open={showManager} onClose={handleManagerClose} />
      </>
    );
  }

  return null;
};

export default FavoritesTopbar;
